import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom';
import backArrow from './assets/back-arrow.png';

const API_URL = process.env.REACT_APP_API_URL || 'https://findai-api-473540340636.us-central1.run.app';

function ItemsTable({ user, idToken }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  // Estado para manejar qué fila está expandida
  const [expandedRowId, setExpandedRowId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'descending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const ITEMS_PER_PAGE = 100;

  useEffect(() => {
    const fetchProtectedData = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_URL}/items_soporte/?limit=1000`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' }
        });
        if (!response.ok) throw new Error('Error al obtener los datos');
        const data = await response.json();
        setItems(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProtectedData();
  }, [user, idToken]);

  const processedItems = useMemo(() => {
    let filteredItems = [...items];

    // Filtrado por búsqueda de nombre
    if (searchTerm) {
      filteredItems = filteredItems.filter(item =>
        item.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrado por fecha de inicio
    if (startDate) {
      const filterStartDate = new Date(startDate.replace(/-/g, '/'));
      filterStartDate.setHours(0, 0, 0, 0); // Compara desde el inicio del día
      filteredItems = filteredItems.filter(item => {
        const itemDate = new Date(item.fecha_creacion);
        return !isNaN(itemDate) && itemDate >= filterStartDate;
      });
    }

    // Filtrado por fecha de fin
    if (endDate) {
      const filterEndDate = new Date(endDate.replace(/-/g, '/'));
      filterEndDate.setHours(23, 59, 59, 999); // Compara hasta el final del día
      filteredItems = filteredItems.filter(item => {
        const itemDate = new Date(item.fecha_creacion);
        return !isNaN(itemDate) && itemDate <= filterEndDate;
      });
    }

    // Ordenamiento
    if (sortConfig.key) {
      filteredItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    return filteredItems;
  }, [items, searchTerm, sortConfig, startDate, endDate]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return processedItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [processedItems, currentPage]);

  const totalPages = Math.ceil(processedItems.length / ITEMS_PER_PAGE);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleRowToggle = (id) => {
    setExpandedRowId(prevId => (prevId === id ? null : id));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'ascending' ? '▲' : '▼';
  }

  if (loading) return <p className="text-center text-gray-500">Cargando datos...</p>;
  if (error) return <p className="text-center text-red-500">{error}</p>;
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4">Registros de Soporte</h2>
      <div className="filters grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <input
          type="text"
          placeholder="Buscar por nombre..."
          className="md:col-span-1 w-full px-4 py-2 border rounded-lg"
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />
        <div className="flex items-center space-x-2">
          <label htmlFor="start-date" className="text-sm font-medium text-gray-700">Desde:</label>
          <input
            type="date"
            id="start-date"
            className="w-full px-4 py-2 border rounded-lg"
            value={startDate}
            onChange={e => { setStartDate(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <div className="flex items-center space-x-2">
          <label htmlFor="end-date" className="text-sm font-medium text-gray-700">Hasta:</label>
          <input
            type="date"
            id="end-date"
            className="w-full px-4 py-2 border rounded-lg"
            value={endDate}
            onChange={e => { setEndDate(e.target.value); setCurrentPage(1); }}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-500 cases-table">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="px-2 py-3"></th>
              <th scope="col" className="px-6 py-3 cursor-pointer" onClick={() => requestSort('id')}>ID {getSortIndicator('id')}</th>
              <th scope="col" className="px-2 py-3 cursor-pointer" onClick={() => requestSort('fecha_creacion')}>Fecha caso {getSortIndicator('fecha_creacion')}</th>
              <th scope="col" className="px-6 py-3 cursor-pointer" onClick={() => requestSort('nombre')}>Nombre {getSortIndicator('nombre')}</th>
              <th scope="col" className="px-6 py-3">Email</th>
              <th scope="col" className="px-6 py-3 cursor-pointer" onClick={() => requestSort('nombre_producto')}>Producto {getSortIndicator('nombre_producto')}</th>
              {/* <th scope="col" className="px-6 py-3" >Asesor</th> */}
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map(item => (
              <React.Fragment key={item.id}>
                <tr className="bg-white border-b hover:bg-gray-50">
                  <td className="px-2 py-4">
                    <button onClick={() => handleRowToggle(item.id)} className="text-lg font-bold btn-expand">
                      {expandedRowId === item.id ? '−' : '+'}
                    </button>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900">
                    <div className="flex items-center justify-between">
                      <span>{item.id}</span>
                      <button onClick={() => {navigator.clipboard.writeText(item.id); setCopiedId(`id-${item.id}`); setTimeout(() => setCopiedId(null), 1000);}} className={`ml-2 px-1 py-1 text-xs rounded transition-colors ${copiedId === `id-${item.id}` ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'}`} title="Copiar">{copiedId === `id-${item.id}` ? '✓' : '📋'}</button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <span>{formatDate(item.fecha_creacion)}</span>
                      <button onClick={() => {navigator.clipboard.writeText(formatDate(item.fecha_creacion)); setCopiedId(`fecha-${item.id}`); setTimeout(() => setCopiedId(null), 1000);}} className={`ml-2 px-1 py-1 text-xs rounded transition-colors ${copiedId === `fecha-${item.id}` ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'}`} title="Copiar">{copiedId === `fecha-${item.id}` ? '✓' : '📋'}</button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <span>{item.nombre}</span>
                      <button onClick={() => {navigator.clipboard.writeText(item.nombre); setCopiedId(`nombre-${item.id}`); setTimeout(() => setCopiedId(null), 1000);}} className={`ml-2 px-1 py-1 text-xs rounded transition-colors ${copiedId === `nombre-${item.id}` ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'}`} title="Copiar">{copiedId === `nombre-${item.id}` ? '✓' : '📋'}</button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <span>{item.email}</span>
                      <button onClick={() => {navigator.clipboard.writeText(item.email); setCopiedId(`email-${item.id}`); setTimeout(() => setCopiedId(null), 1000);}} className={`ml-2 px-1 py-1 text-xs rounded transition-colors ${copiedId === `email-${item.id}` ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'}`} title="Copiar">{copiedId === `email-${item.id}` ? '✓' : '📋'}</button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <span>{item.nombre_producto}</span>
                      <button onClick={() => {navigator.clipboard.writeText(item.nombre_producto); setCopiedId(`producto-${item.id}`); setTimeout(() => setCopiedId(null), 1000);}} className={`ml-2 px-1 py-1 text-xs rounded transition-colors ${copiedId === `producto-${item.id}` ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'}`} title="Copiar">{copiedId === `producto-${item.id}` ? '✓' : '📋'}</button>
                    </div>
                  </td>
                </tr>
                {expandedRowId === item.id && (
                  <tr>
                    <td colSpan="6">
                      <ItemDetails item={item} copiedId={copiedId} setCopiedId={setCopiedId} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-gray-700">Página {currentPage} de {totalPages}</span>
        <div>
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-l hover:bg-gray-900 disabled:bg-gray-400">Anterior</button>
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-r border-0 border-l border-gray-700 hover:bg-gray-900 disabled:bg-gray-400">Siguiente</button>
        </div>
      </div>
    </div>
  );
}

function ItemDetails({ item, copiedId, setCopiedId }) {
  return (
    <div className="details bg-gray-100 p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <DetailItem label="Teléfono" value={item.telefono} copiedId={copiedId} setCopiedId={setCopiedId} />
      <DetailItem label="Código de Barras" value={item.codigo_barras} copiedId={copiedId} setCopiedId={setCopiedId} />
      <DetailItem label="Código de Lote" value={item.codigo_lote} copiedId={copiedId} setCopiedId={setCopiedId} />
      <DetailItem label="Fecha de Vencimiento" value={item.fecha_vencimiento} copiedId={copiedId} setCopiedId={setCopiedId} />
      <DetailItem label="Fecha de Compra" value={item.fecha_compra} copiedId={copiedId} setCopiedId={setCopiedId} />
      <DetailItem label="Dirección" value={item.direccion} copiedId={copiedId} setCopiedId={setCopiedId} />
      <DetailItem label="Tipo de Reemplazo" value={item.tipo_reemplazo} copiedId={copiedId} setCopiedId={setCopiedId} />
      <DetailItem label="Horario de Contacto" value={item.horario} copiedId={copiedId} setCopiedId={setCopiedId} />
      <DetailItem label="Asesor" value={item.asesor} copiedId={copiedId} setCopiedId={setCopiedId} />
      <div className="md:col-span-2 lg:col-span-3">
        <DetailItem label="Detalle del Caso" value={item.detalle} copiedId={copiedId} setCopiedId={setCopiedId} />
      </div>
      <div className="flex space-x-4">
        <BooleanItem label="Usuario Molesto" value={item.usuario_molesto} />
        <BooleanItem label="Es Influencer" value={item.usuario_influencer} />
        <BooleanItem label="Riesgo de Salud" value={item.riesgo_salud} />
      </div>
      <div className="flex space-x-4">
        <BooleanItem label="Presenta Muestra" value={item.muestra_compra} />
        <BooleanItem label="Pide Sustitución" value={item.solicita_sustitucion} />
        <BooleanItem label="Atención en Planta" value={item.atencion_planta} />
      </div>
    </div>
  );
}


const DetailItem = ({ label, value, copiedId, setCopiedId }) => {
  const copyId = `${label}-${value}`;
  const copyToClipboard = () => {
    navigator.clipboard.writeText(value || 'No especificado');
    setCopiedId(copyId);
    setTimeout(() => setCopiedId(null), 1000);
  };

  return (
    <div className="detail-item flex items-center justify-between">
      <p>
        <span className="text-sm font-semibold text-gray-600">{label}: </span>
        <span className="text-md text-gray-800">{value || 'No especificado'}</span>
      </p>
      <button 
        onClick={copyToClipboard}
        className={`ml-2 px-2 py-1 text-xs rounded transition-colors ${
          copiedId === copyId ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'
        }`}
        title="Copiar al portapapeles"
      >
        {copiedId === copyId ? '✓' : '📋'}
      </button>
    </div>
  );
};

const BooleanItem = ({ label, value }) => (
  <div className="boolean-item">
    <p className="text-sm font-semibold text-gray-600">{label}</p>
    <span className={`px-2 py-1 text-sm font-bold rounded-full ${value ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
      {value ? 'Sí' : 'No'}
    </span>
  </div>
);

const Casos = ({ user, idToken }) => {
  return (
    <>
      <Helmet>
        <title>Casos atendidos</title>
      </Helmet>
      <div className="breadcrumb">
        <Link to="/" ><img src={backArrow}></img> Casos atendidos</Link>
      </div>
      <div className="body">
        <ItemsTable user={user} idToken={idToken} />
      </div>
    </>
  );
};

export default Casos;
